// Grid geometry: cumulative pixel offsets for rows and columns, and the
// visible-window lookup that drives virtualisation.
//
// Widths are overwhelmingly the default, so the prefix sums are rebuilt from
// the sparse override maps and cached against a version stamp rather than
// maintained incrementally.

import type { Sheet } from '@/model/types';
import { DEFAULT_COL_WIDTH, DEFAULT_ROW_HEIGHT } from '@/model/workbook';

export const HEADER_H = 24;
export const HEADER_W = 48;

export interface Axis {
  /** offsets[i] is the pixel start of index i+1 (1-based); length = count + 1. */
  offsets: Float64Array;
  count: number;
  total: number;
}

export function buildAxis(count: number, sizeOf: (i: number) => number): Axis {
  const offsets = new Float64Array(count + 1);
  let acc = 0;
  for (let i = 1; i <= count; i++) {
    offsets[i - 1] = acc;
    acc += sizeOf(i);
  }
  offsets[count] = acc;
  return { offsets, count, total: acc };
}

/**
 * A hidden row or column is size ZERO, not absent.
 *
 * Everything downstream — offsets, hit-testing, the visible window, the
 * selection rectangle — is derived from the axis, so hiding is expressed once,
 * here, and the rest of the grid needs to know nothing about it.
 *
 * The spare indices past the end of the sheet are zero too. They exist so an
 * offset, a fill preview or an overrun running off the last column has
 * somewhere to land, not to give the canvas twenty rows of ground nobody can
 * click on — which is what they were, and what a filter that hid every blank
 * row left conspicuously behind.
 */
export function colAxis(sheet: Sheet, extra = 4): Axis {
  const count = sheet.cols + extra;
  return buildAxis(count, (c) =>
    c > sheet.cols || sheet.hiddenCols?.[c] ? 0 : sheet.colWidths[c] ?? DEFAULT_COL_WIDTH
  );
}

/**
 * `filtered` is folded in here with the hidden rows, for the same reason they
 * are: everything downstream reads the axis, so a row that is out is out once,
 * in one place, and the rest of the grid needs to know nothing about filters.
 */
export function rowAxis(
  sheet: Sheet,
  extra = 20,
  filtered?: ReadonlySet<number>,
  /**
   * Pixels hung beneath a row that are not a row of their own — a database's
   * sub-rows. They belong to the row's band, so the row numbering below never
   * moves; `cellHeight` is the row without them.
   */
  below?: (r: number) => number
): Axis {
  const count = sheet.rows + extra;
  return buildAxis(count, (r) =>
    r > sheet.rows || sheet.hiddenRows?.[r] || filtered?.has(r)
      ? 0
      : (sheet.rowHeights[r] ?? DEFAULT_ROW_HEIGHT) + (below?.(r) ?? 0)
  );
}

/** A row's own height, without anything hung beneath it. */
export function cellHeight(axis: Axis, r: number, below?: (r: number) => number): number {
  const size = sizeAt(axis, r);
  return size === 0 ? 0 : size - (below?.(r) ?? 0);
}

export function sizeAt(axis: Axis, i: number): number {
  if (i < 1 || i > axis.count) return 0;
  return axis.offsets[i]! - axis.offsets[i - 1]!;
}

export function offsetAt(axis: Axis, i: number): number {
  if (i < 1) return 0;
  if (i > axis.count) return axis.total;
  return axis.offsets[i - 1]!;
}

/** 1-based index whose band contains `px`, clamped to the axis. */
export function indexAt(axis: Axis, px: number): number {
  if (px <= 0) return 1;
  if (px >= axis.total) return axis.count;
  let lo = 0;
  let hi = axis.count;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (axis.offsets[mid - 1]! <= px) lo = mid;
    else hi = mid - 1;
  }
  return Math.max(1, lo);
}

/**
 * The pixel box a range [r1..r2, c1..c2] occupies — the selection box, a
 * merge's own box, a formula-reference box, the pointing box and the fill
 * preview all need exactly this, so it lives here once rather than six times.
 * Named `rangeRect`, not `rectOf` — `ui/Popover.tsx` already exports a
 * same-named `rectOf` for a DOM element's anchor rect, and Grid.tsx imports
 * both.
 */
export function rangeRect(
  cols: Axis,
  rows: Axis,
  cellH: (r: number) => number,
  r1: number,
  c1: number,
  r2: number,
  c2: number
): { x: number; y: number; w: number; h: number } {
  const x = offsetAt(cols, c1);
  const y = offsetAt(rows, r1);
  return {
    x,
    y,
    w: offsetAt(cols, c2) + sizeAt(cols, c2) - x,
    h: offsetAt(rows, r2) + cellH(r2) - y,
  };
}

export interface Window {
  first: number;
  last: number;
}

/** Visible index range for a viewport, with a small overscan. */
export function windowOf(axis: Axis, scroll: number, viewport: number, overscan = 3): Window {
  const first = Math.max(1, indexAt(axis, scroll) - overscan);
  const last = Math.min(axis.count, indexAt(axis, scroll + viewport) + overscan);
  return { first, last };
}
